import { motion } from 'framer-motion'
import { Calendar, User, ArrowRight, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { blogPosts } from '../data/blogPosts'
import SEO from '../components/SEO'

const Blog = () => {
  const { t, language } = useLanguage()

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(language === 'sq' ? 'sq-AL' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <>
      <SEO 
        title={t('seo.blog.title')}
        description={t('seo.blog.description')}
        keywords={t('seo.blog.keywords')}
      />
      <div className="mt-20">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark relative overflow-hidden">
        <motion.div
          animate={{ x: [0, 100, 0], y: [0, 50, 0], scale: [1, 1.2, 1], opacity: [0.03, 0.08, 0.03] }}
          transition={{ duration: 25, repeat: Infinity }}
          className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-bronze rounded-full blur-3xl"
        />
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="section-title mb-6">
              {t('blog.title')} <span className="text-gradient-gold">{t('blog.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-beige-200 max-w-3xl mx-auto">
              {t('blog.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-24 bg-gradient-to-b from-dark to-bronze-950 relative overflow-hidden">
        <motion.div
          animate={{
            x: [0, -50, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
            opacity: [0.05, 0.12, 0.05]
          }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute top-20 right-20 w-96 h-96 bg-gradient-gold rounded-full blur-3xl"
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className="bg-bronze-900/30 backdrop-blur-sm rounded-2xl overflow-hidden border border-gold-400/20 hover:border-gold-400/40 transition-all duration-500 hover:transform hover:scale-105">
                    {/* Image */}
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title[language]}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-4 py-1.5 bg-gold-400 text-dark text-sm font-semibold rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      {/* Meta */}
                      <div className="flex items-center gap-4 text-sm text-beige-300 mb-4">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(post.date)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />
                          <span>5 {language === 'sq' ? 'min' : 'min'}</span>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-bold text-cream mb-3 group-hover:text-gold-300 transition-colors">
                        {post.title[language]}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-beige-200 mb-4 line-clamp-2">
                        {post.excerpt[language]}
                      </p>

                      {/* Read More */}
                      <div className="flex items-center gap-2 text-gold-400 font-semibold group-hover:gap-3 transition-all">
                        <span>{language === 'sq' ? 'Lexo më shumë' : 'Read more'}</span>
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      </div>
    </>
  )
}

export default Blog
